'use client'

import { useState, useEffect } from 'react'
import StatCards from './StatCards'
import { formatFontFamily } from '@/lib/themeHelpers'
import { useScrollAnimation, scrollAnimationClasses } from '@/lib/useScrollAnimation'
import { useAssistant } from '@/lib/assistantContext'

interface HeroSectionProps {
  theme: any;
  radius: number;
  fontFamily: string;
  heroImages: {
    desktop: string;
    mobile: string;
  };
  content: any;
}

export default function HeroSection({
  theme,
  radius,
  fontFamily,
  heroImages,
  content
}: HeroSectionProps) {
  const { openPanel } = useAssistant();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();
  const { ref: imageRef, isVisible: imageVisible } = useScrollAnimation({ rootMargin: '0px 0px -100px 0px' });
  const [scrollY, setScrollY] = useState(0);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const checkScreenSize = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
    
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section 
      className="relative isolate overflow-hidden min-h-screen" 
      style={{ 
        background: '#FFFFFF',
        fontFamily: formatFontFamily(fontFamily)
      }}
    >
      {/* Flattened Grid Layout - All elements are direct children for proper mobile ordering */}
      <div 
        ref={contentRef as React.RefObject<HTMLDivElement>}
        className={`mx-auto max-w-6xl px-4 pt-6 pb-12 sm:px-6 lg:px-8 lg:pt-12 lg:pb-16 grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-10 items-start lg:min-h-[70vh] ${scrollAnimationClasses.transition} ${contentVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
      >
        {/* Tag - Order 1 */}
        <div className="order-1 lg:col-start-1 flex justify-center lg:justify-start">
          <div 
            style={{
              display: 'inline-flex',
              background: '#E8EDF5',
              color: '#103065',
              padding: '8px 16px',
              borderRadius: radius,
              fontSize: '14px',
              fontWeight: '700',
              letterSpacing: '2%',
              fontFamily: 'Arial, Helvetica, sans-serif',
              alignItems: 'center',
              gap: '8px',
              width: 'fit-content',
              border: '1px solid #103065'
            }}
          >
            {content.tag}
          </div>
        </div>
        
        {/* Title - Order 2 */}
        <h1 className="order-2 lg:col-start-1 text-[2.25rem] font-semibold leading-[1.1] md:text-[2.75rem] lg:text-[3rem] xl:text-[3.5rem] font-primary text-center lg:text-left max-w-full lg:max-w-[480px] whitespace-pre-line" style={{ color: '#103065' }}>
          {content.title}
        </h1>
        
        {/* Description - Order 3 */}
        <p className="order-3 lg:col-start-1 text-base sm:text-lg leading-relaxed font-secondary text-center lg:text-left" style={{ color: theme.mutedText }}>
          {content.description}
        </p>
        
        {/* Hero Image - Order 4 on mobile, spans all rows on desktop in column 2 */}
        <div 
          ref={imageRef as React.RefObject<HTMLDivElement>}
          className={`order-4 lg:order-1 lg:col-start-2 lg:row-span-5 relative ${scrollAnimationClasses.transition} ${imageVisible ? scrollAnimationClasses.visible : scrollAnimationClasses.hidden}`}
          style={{ transitionDelay: '200ms' }}
        >
          <div 
            style={{
              transform: isDesktop ? `translateY(${scrollY * 0.1}px)` : 'none',
              transition: 'transform 0.1s ease-out'
            }}
          >
            <div 
              className="w-full h-[280px] sm:h-[300px] md:h-[400px] lg:h-[600px] rounded-lg shadow-2xl ring-1 ring-black/5 overflow-hidden transition-shadow duration-500 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] cursor-pointer group"
              style={{ borderRadius: radius }}
            >
              <picture>
                <source 
                  media="(min-width: 1024px)" 
                  srcSet={heroImages.desktop} 
                />
                <img 
                  src={heroImages.mobile}
                  alt="Colorful torn paper rainbow stripes flowing diagonally"
                  className="w-full h-full object-cover object-[30%_20%] sm:object-[40%_30%] lg:object-[50%_30%] transition-transform duration-500 group-hover:scale-105"
                />
              </picture>
            </div>
          </div>
        </div>
        
        {/* Stats - Order 5 */}
        <div className="order-5 lg:col-start-1 mt-2 lg:mt-4">
          <StatCards theme={theme} radius={radius} stats={content.stats} />
        </div>
        
        {/* CTA Button - Order 6 */}
        <div className="order-6 lg:col-start-1 mt-2 lg:mt-4 flex justify-center lg:justify-start">
          <button 
            className="w-full sm:w-auto px-6 py-3 text-base font-semibold shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] font-primary" 
            style={{ 
              background: theme.button, 
              color: theme.onButton, 
              borderRadius: radius,
              border: 'none'
            }}
            onClick={openPanel}
          >
            {content.ctaText}
          </button>
        </div>
      </div>
    </section>
  );
}
